import { ValidationError } from "yup";
import { validator } from "@/utils/validator";
import type { TBulkSyncFilterForm } from "@/features/learner/components/learner-list/bulk-sync-filter";

// irohana visa 取り込みフィルタ「1号経過期間」の入力バリデーション。
// learnerReportFilterSchema の「一号残期間」と同じ上限 (年 ≤ 4 / 月 ≤ 11)。
// from / to それぞれ (年 / 月) の2数値入力で、月レンジ (skillOnePeriodMonthsFrom/To) として送る。
export const SKILL_ONE_PERIOD_MAX_YEARS = 4;
export const SKILL_ONE_PERIOD_MAX_MONTHS = 11;

// 数字以外の入力 / 上限超過時のエラーメッセージ (年 / 月それぞれ別)。
export const SKILL_ONE_PERIOD_YEAR_ERROR = `整数を入力し、${SKILL_ONE_PERIOD_MAX_YEARS}以下の値にしてください。`;
export const SKILL_ONE_PERIOD_MONTH_ERROR = `整数を入力し、${SKILL_ONE_PERIOD_MAX_MONTHS}以下の値にしてください。`;
// 終了 < 開始 のときのエラーメッセージ。
export const SKILL_ONE_PERIOD_RANGE_ERROR = "「1号経過期間」の終了は開始以降を入力してください。";

// 年+月 → 合計月数。両方未入力なら null (=条件なし)。
export const toSkillOnePeriodMonths = (year: string, month: string): number | null => {
  if (year.trim() === "" && month.trim() === "") return null;
  return (Number(year) || 0) * 12 + (Number(month) || 0);
};

type TSkillOnePeriodFields = Pick<
  TBulkSyncFilterForm,
  "skillOnePeriodYearFrom" | "skillOnePeriodMonthFrom" | "skillOnePeriodYearTo" | "skillOnePeriodMonthTo"
>;

// 年・月それぞれ: 空 (未入力) or「半角数字のみ (小数点/マイナス/文字不可) かつ上限以下」なら有効。
// number() は "11." を 11 に coerce して通してしまうため、生文字列を matches(/^\d+$/) で厳密判定する。
const skillOnePeriodPart = (max: number, message: string) =>
  validator
    .string()
    .emptyToNull()
    .nullable()
    .notRequired()
    .matches(/^\d+$/, { message, excludeEmptyString: true })
    .test("skill-one-period-max", message, (v) => v == null || Number(v) <= max);

// パネルの「1号経過期間」(from/to の 年 / 月) バリデーションスキーマ。各パートを独立に検証する。
export const bulkSyncSkillOnePeriodSchema = validator.object({
  skillOnePeriodYearFrom: skillOnePeriodPart(SKILL_ONE_PERIOD_MAX_YEARS, SKILL_ONE_PERIOD_YEAR_ERROR),
  skillOnePeriodMonthFrom: skillOnePeriodPart(SKILL_ONE_PERIOD_MAX_MONTHS, SKILL_ONE_PERIOD_MONTH_ERROR),
  skillOnePeriodYearTo: skillOnePeriodPart(SKILL_ONE_PERIOD_MAX_YEARS, SKILL_ONE_PERIOD_YEAR_ERROR),
  skillOnePeriodMonthTo: skillOnePeriodPart(SKILL_ONE_PERIOD_MAX_MONTHS, SKILL_ONE_PERIOD_MONTH_ERROR),
});

// スキーマからフィールドごとの error メッセージを取得する (Input の error 表示用)。
// error(赤枠) と canApply を同じスキーマから導出し、判定を必ず一致させる (単一ソース)。
export const getSkillOnePeriodErrors = (f: TSkillOnePeriodFields): Partial<Record<keyof TSkillOnePeriodFields, string>> => {
  try {
    bulkSyncSkillOnePeriodSchema.validateSync(f, { abortEarly: false });
    return {};
  } catch (e) {
    const errors: Partial<Record<keyof TSkillOnePeriodFields, string>> = {};
    if (e instanceof ValidationError) {
      for (const err of e.inner) {
        const path = err.path as keyof TSkillOnePeriodFields | undefined;
        if (path && !errors[path]) errors[path] = err.message;
      }
    }
    return errors;
  }
};

// 終了(to) >= 開始(from) か。両方入力時のみ判定 (片方のみ or 未入力は有効)。
export const isSkillOnePeriodRangeValid = (f: TSkillOnePeriodFields): boolean => {
  const from = toSkillOnePeriodMonths(f.skillOnePeriodYearFrom, f.skillOnePeriodMonthFrom);
  const to = toSkillOnePeriodMonths(f.skillOnePeriodYearTo, f.skillOnePeriodMonthTo);
  if (from == null || to == null) return true;
  return to >= from;
};

// 全パートの整数/上限 (スキーマ) + レンジ (to >= from) が有効か (確定可否の判定用)。
export const isSkillOnePeriodValid = (f: TSkillOnePeriodFields): boolean =>
  bulkSyncSkillOnePeriodSchema.isValidSync(f) && isSkillOnePeriodRangeValid(f);
